import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Avatar,
  Button
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContactModal from '../../components/ContactModal';
import { getContactInfo } from '../../utils/iframe';

const CasesPage = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const contactInfo = getContactInfo();

  const handleContactModalOpen = () => {
    setContactModalOpen(true);
  };

  const handleContactModalClose = () => {
    setContactModalOpen(false);
  };

  const casesData = [
    {
      id: 1,
      title: "자체 제품 개발 및 판매 업체",
      subtitle: "B2C SaaS 기반 고객 지원 자동화",
      category: "기업",
      icon: <BusinessIcon />,
      description: "고객센터 운영 인력 부족과 FAQ 처리 시간 낭비 문제를 RAG 시스템으로 해결하여 고객 대응 속도를 획기적으로 개선했습니다.",
      problems: [
        "고객센터 운영 인력 부족",
        "FAQ 방대 중대에 시간 낭비", 
        "고객 대응 속도 저하"
      ],
      solutions: [
        "사내 문서 + 제품 매뉴얼 기반",
        "RAG 시스템 구축",
        "웹사이트 접목 → 자동 응답"
      ],
      results: [
        { metric: "응답시간", before: "2시간", after: "10분", improvement: "90% 단축" },
        { metric: "콜센터 문량", improvement: "50% 감소" },
        { metric: "직원 만족도", improvement: "30% 상승" }
      ],
      duration: "1주일",
      tags: ["B2C SaaS", "고객지원", "자동화"],
      backgroundColor: "#1976D2"
    },
    {
      id: 2,
      title: "대학교 학부 홈페이지",
      subtitle: "공학원 연구실 지식 관리 체계화",
      category: "연구기관",
      icon: <SchoolIcon />,
      description: "10여년간 누적된 연구 데이터의 검색 어려움을 해결하고, 연구실 멘토링 부담을 대폭 줄인 지식관리 시스템을 구축했습니다.",
      problems: [
        "10여년간 누적된 데이터 검색 어려움",
        "논문 자료 활용 비효율",
        "변칙 접근으로 시간 낭비"
      ],
      solutions: [
        "3,000개 학회이지, 7,700개 문서파일, 30,000장 DB레코드",
        "데이터 수집 및 벡터 DB 구축 RAG 기반 Q&A 챗봇",
        "자연어 기반 지식 검색"
      ],
      results: [
        { metric: "검색 시간", improvement: "10배 단축" },
        { metric: "멘토링 부담", improvement: "70% 감소" },
        { metric: "과제 효율", improvement: "향상" }
      ],
      duration: "2주",
      tags: ["연구실", "지식관리", "학술"],
      backgroundColor: "#388E3C"
    },
    {
      id: 3,
      title: "스포츠 용품 쇼핑몰",
      subtitle: "제품 검색 및 추천 질의응답 시스템",
      category: "쇼핑몰",
      icon: <BusinessIcon />,
      description: "고객 FAQ 과부하와 상품 정보 활용 부족 문제를 해결하여 고객 대응 속도를 대폭 향상시키고 마케팅 효율을 개선했습니다.",
      problems: [
        "고객 FAQ 중대 과부하",
        "인기 상품/지역 정보 활용 부족",
        "고객 문의 자료 비효율"
      ],
      solutions: [
        "문서 기반자동 응답",
        "FAQ 기반챗봇 도입",
        "시간절약 기반 응답(자동/검토)"
      ],
      features: [
        { name: "고객 문의 자료 응답자동", icon: "🔍" },
        { name: "판매 트렌드 자료 응답", icon: "📊" },
        { name: "지역별 구매 지표 응답", icon: "🎯" }
      ],
      results: [
        { metric: "재접고 문의 검색시간", improvement: "80% 단축" },
        { metric: "마케팅 타켓 효율", improvement: "30% 이상 증가" },
        { metric: "고객대응 속도", improvement: "대폭 향상" }
      ],
      duration: "2주 (데이터 정제 + 시각화 포함)",
      tags: ["이커머스", "제품추천", "자동응답"],
      backgroundColor: "#D32F2F"
    }
  ];

  const getCategoryColor = (category, backgroundColor) => {
    if (backgroundColor) {
      return backgroundColor;
    }
    switch (category) {
      case "기업":
        return "#1565C0";
      case "연구기관":
        return "#388E3C";
      case "쇼핑몰":
        return "#D32F2F";
      default:
        return "#37474F";
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      padding: '24px 16px',
      margin: 0,
      boxSizing: 'border-box'
    }}>
      {/* 헤더 섹션 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1565C0 0%, #37474F 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          적용 사례
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}
        >
          다양한 기업과 기관에서 활용되고 있는 빅스데이터 AI 기반 질의응답 시스템의 성공 사례들을 소개합니다
        </Typography>
      </Box>



      {/* 사례 카드 섹션 */}
      <Grid container spacing={4}>
        {casesData.map((caseItem) => (
          <Grid item xs={12} key={caseItem.id}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
                },
                mb: 3
              }}
            >
              {/* 헤더 영역 */}
              <CardMedia
                component="div"
                sx={{
                  height: 100,
                  background: `linear-gradient(135deg, ${caseItem.backgroundColor} 0%, ${caseItem.backgroundColor}CC 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  position: 'relative',
                  color: 'white',
                  px: 3
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      mr: 2,
                      '& .MuiSvgIcon-root': {
                        fontSize: 26,
                        color: 'white'
                      }
                    }}
                  >
                    {caseItem.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 800, 
                      mb: 0.5,
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      color: 'white'
                    }}>
                      {caseItem.title}
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      opacity: 0.95,
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      color: 'white',
                      fontWeight: 500
                    }}>
                      {caseItem.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={caseItem.category}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                    fontWeight: 'bold',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    '& .MuiChip-label': {
                      fontWeight: 700
                    }
                  }}
                />
              </CardMedia>

              <CardContent sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, fontSize: '1rem' }}>
                  {caseItem.description}
                </Typography>

                <Grid container spacing={3}>
                  {/* 좌측 열 - 문제점과 해결방안 */}
                  <Grid item xs={12} md={6}>
                    {/* 주요 문제점 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
                        🚨 주요 문제점
                      </Typography>
                      <Box>
                        {caseItem.problems.map((problem, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.8, display: 'flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ 
                              width: 5, 
                              height: 5, 
                              borderRadius: '50%', 
                              backgroundColor: '#ff5722', 
                              mr: 1.2,
                              flexShrink: 0
                            }} />
                            {problem}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    {/* 해결 방안 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
                        💡 해결 방안
                      </Typography>
                      <Box>
                        {caseItem.solutions.map((solution, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 0.8, display: 'flex', alignItems: 'center' }}>
                            <Box component="span" sx={{ 
                              width: 5, 
                              height: 5, 
                              borderRadius: '50%', 
                              backgroundColor: caseItem.backgroundColor, 
                              mr: 1.2,
                              flexShrink: 0
                            }} />
                            {solution}
                          </Typography>
                        ))}
                      </Box>
                    </Box>

                    {/* 주요 기능 (쇼핑몰 사례만) */}
                    {caseItem.features && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
                          ⚙️ 주요 기능
                        </Typography>
                        <Grid container spacing={1}>
                          {caseItem.features.map((feature, index) => (
                            <Grid item xs={12} key={index}>
                              <Box sx={{ 
                                p: 1.5, 
                                borderRadius: 1, 
                                backgroundColor: 'rgba(0,0,0,0.03)',
                                display: 'flex',
                                alignItems: 'center'
                              }}>
                                <Typography variant="h6" sx={{ mr: 1.5 }}>{feature.icon}</Typography>
                                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                                  {feature.name}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    )}
                  </Grid>

                  {/* 우측 열 - 성과와 기간 */}
                  <Grid item xs={12} md={6}>
                    {/* 달성 성과 */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', fontSize: '1.1rem' }}>
                        📈 달성 성과
                      </Typography>
                      <Grid container spacing={1.5}>
                        {caseItem.results.map((result, index) => (
                          <Grid item xs={12} key={index}>
                            <Box sx={{ 
                              p: 2, 
                              borderRadius: 2, 
                              background: `linear-gradient(135deg, ${caseItem.backgroundColor}15 0%, ${caseItem.backgroundColor}08 100%)`,
                              border: `1px solid ${caseItem.backgroundColor}30`,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                {result.metric}
                              </Typography>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: caseItem.backgroundColor }}>
                                  {result.improvement}
                                </Typography>
                                {result.before && result.after && (
                                  <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                    {result.before} → {result.after}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* 구축 기간 & 태그 */}
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          구축 기간
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: caseItem.backgroundColor }}>
                          {caseItem.duration}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {caseItem.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{
                              backgroundColor: `${caseItem.backgroundColor}15`,
                              color: caseItem.backgroundColor,
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 문의 섹션 */}
      <Box sx={{ textAlign: 'center', mt: 8, p: 4, backgroundColor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          귀하의 조직에도 적용해보세요
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          빅스데이터 AI 기반 질의응답 시스템 도입에 대한 상담을 받아보세요
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={handleContactModalOpen}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            backgroundColor: '#1565C0',
            '&:hover': {
              backgroundColor: '#0D47A1'
            }
          }}
        >
          도입 상담 문의
        </Button>
      </Box>

      {/* 도입 상담 문의 모달 */}
      <ContactModal 
        open={contactModalOpen} 
        onClose={handleContactModalClose}
        contactInfo={contactInfo}
      />
    </Box>
  );
};

export default CasesPage;